import { C as Component } from "./component-DqhvLgtR.js";
import { T as ThemeEvents } from "./events-C4hlH4zR.js";
import {
  d as defineCustomElement,
  r as requestIdleCallback,
  e as escapeHtml,
  f as formatMoney,
} from "./utilities-DuXQiAqj.js";
class CartUpsellsComponent extends Component {
  #mutationObserver = null;
  #processed = false;
  #hiddenBecauseCartEmpty = false;
  /** @type {HTMLElement | null} */
  #recommendationsEl = null;
  /** @type {((event: Event) => void) | null} */
  #jsonRecommendationsHandler = null;
  /** @type {(() => void) | null} */
  #liquidAjaxInitHandler = null;
  connectedCallback() {
    super.connectedCallback();
    this.#syncVisibilityWithCart();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#liquidAjaxInitHandler = () => {
      const itemCount = window.liquidAjaxCart?.cart?.item_count;
      if (typeof itemCount === "number") {
        this.#syncVisibilityWithCart(itemCount);
      }
    };
    document.addEventListener("liquid-ajax-cart:init", this.#liquidAjaxInitHandler);
    this.#initRecommendationsWatcher();
    this.#wireExternalNav();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    if (this.#liquidAjaxInitHandler) {
      document.removeEventListener("liquid-ajax-cart:init", this.#liquidAjaxInitHandler);
      this.#liquidAjaxInitHandler = null;
    }
    if (this.#recommendationsEl && this.#jsonRecommendationsHandler) {
      this.#recommendationsEl.removeEventListener(
        "recommendations-json-loaded",
        this.#jsonRecommendationsHandler
      );
      this.#jsonRecommendationsHandler = null;
    }
    this.#recommendationsEl = null;
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }
  /**
   * Wire the external prev/next buttons (rendered in the cart-upsells heading row)
   * to the embla carousel instance. Uses event delegation so it survives re-renders.
   *
   * Note: this theme uses a custom Embla wrapper with `goToPrev`/`goToNext` (not
   * Embla's native `scrollPrev`/`scrollNext`), so we call those here.
   */
  #wireExternalNav() {
    const wrapper = this.closest(".cart-upsells");
    if (!wrapper) {
      return;
    }
    const getCarousel = () => this.querySelector("carousel-embla");
    const updateDisabled = () => {
      const carousel = getCarousel();
      const embla = carousel?.embla;
      const prev = wrapper.querySelector(".cart-upsells-nav__prev");
      const next = wrapper.querySelector(".cart-upsells-nav__next");
      if (!embla || !prev || !next) {
        return;
      }
      const isLoop = carousel?.dataset?.loop !== "false";
      if (isLoop) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }
      const snapIndex = typeof embla.selectedSnap === "function" ? embla.selectedSnap() : 0;
      const snapCount =
        (typeof embla.scrollSnapList === "function" && embla.scrollSnapList().length) ||
        (typeof embla.slideNodes === "function" && embla.slideNodes().length) ||
        0;
      prev.disabled = snapIndex <= 0;
      next.disabled = snapCount > 0 && snapIndex >= snapCount - 1;
    };
    wrapper.addEventListener("click", (event) => {
      const target =
        /** @type {HTMLElement} */
        event.target;
      const btn = target.closest?.(".cart-upsells-nav__prev, .cart-upsells-nav__next");
      if (!btn) {
        return;
      }
      event.preventDefault();
      const carousel = getCarousel();
      const embla = carousel?.embla;
      if (!embla) {
        return;
      }
      if (btn.classList.contains("cart-upsells-nav__prev")) {
        if (typeof embla.goToPrev === "function") {
          embla.goToPrev();
        } else if (typeof embla.scrollPrev === "function") {
          embla.scrollPrev();
        }
      } else {
        if (typeof embla.goToNext === "function") {
          embla.goToNext();
        } else if (typeof embla.scrollNext === "function") {
          embla.scrollNext();
        }
      }
    });
    let tries = 0;
    const tick = () => {
      const carousel = getCarousel();
      const embla = carousel?.embla;
      if (embla) {
        updateDisabled();
        embla.on("select", updateDisabled);
        embla.on("reinit", updateDisabled);
        return;
      }
      if (tries++ < 20) {
        setTimeout(tick, 150);
      }
    };
    tick();
  }
  /**
   * Keep upsells hidden when the cart is empty (also after Ajax updates).
   * @param {number} [itemCountOverride]
   */
  #syncVisibilityWithCart(itemCountOverride) {
    const cartUpsells = this.closest(".cart-upsells");
    if (!cartUpsells) {
      return;
    }
    if (typeof itemCountOverride !== "number" && !window.liquidAjaxCart?.cart) {
      return;
    }
    const itemCount =
      typeof itemCountOverride === "number"
        ? itemCountOverride
        : (window.liquidAjaxCart?.cart?.item_count ??
          window.liquidAjaxCart?.cart?.items?.length ??
          0);
    if (itemCount <= 0) {
      cartUpsells.style.display = "none";
      this.#hiddenBecauseCartEmpty = true;
      return;
    }
    if (this.#hiddenBecauseCartEmpty) {
      cartUpsells.style.display = "";
      this.#hiddenBecauseCartEmpty = false;
    }
  }
  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    const itemCountFromEvent =
      event?.detail?.data?.itemCount ?? event?.detail?.resource?.item_count;
    this.#syncVisibilityWithCart(
      typeof itemCountFromEvent === "number" ? itemCountFromEvent : void 0
    );
  };
  /**
   * Watch for product-recommendations element to load and convert to carousel
   */
  #initRecommendationsWatcher() {
    const recommendationsEl = this.querySelector("product-recommendations");
    if (!recommendationsEl) {
      return;
    }
    this.#recommendationsEl = recommendationsEl;
    if (recommendationsEl.dataset.carouselProcessed === "true") {
      return;
    }
    this.#jsonRecommendationsHandler = (event) => {
      if (this.#processed) {
        return;
      }
      const { products } =
        /** @type {CustomEvent} */
        event.detail;
      requestIdleCallback(
        () => {
          this.#renderJsonRecommendations(recommendationsEl, products);
        },
        { timeout: 2e3 }
        // Max 2s delay to ensure it renders
      );
    };
    recommendationsEl.addEventListener(
      "recommendations-json-loaded",
      this.#jsonRecommendationsHandler
    );
    if (
      recommendationsEl.dataset.recommendationsPerformed === "true" &&
      recommendationsEl.dataset.recommendationsJson === "true"
    );
    this.#mutationObserver = new MutationObserver(() => {
      if (this.#processed) {
        return;
      }
      if (
        recommendationsEl.dataset.recommendationsPerformed === "true" &&
        recommendationsEl.dataset.recommendationsJson === "true" &&
        recommendationsEl.innerHTML.trim().length === 0 &&
        recommendationsEl.dataset.recommendationsProducts
      ) {
        try {
          const products = JSON.parse(recommendationsEl.dataset.recommendationsProducts);
          this.#renderJsonRecommendations(recommendationsEl, products);
          return;
        } catch (e) {
          console.error("Error parsing stored recommendations JSON:", e);
        }
      }
      const hasContent =
        recommendationsEl.innerHTML.trim().length > 0 &&
        recommendationsEl.dataset.recommendationsPerformed === "true" &&
        recommendationsEl.dataset.recommendationsJson !== "true";
      if (hasContent) {
        this.#wrapInCarousel(recommendationsEl);
      }
    });
    this.#mutationObserver.observe(recommendationsEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-recommendations-performed", "data-recommendations-json"],
    });
    if (
      recommendationsEl.dataset.recommendationsPerformed === "true" &&
      recommendationsEl.dataset.recommendationsJson === "true" &&
      recommendationsEl.dataset.recommendationsProducts
    ) {
      try {
        const products = JSON.parse(recommendationsEl.dataset.recommendationsProducts);
        this.#renderJsonRecommendations(recommendationsEl, products);
        return;
      } catch (e) {
        console.error("Error parsing stored recommendations JSON:", e);
      }
    }
    if (
      recommendationsEl.dataset.recommendationsPerformed === "true" &&
      recommendationsEl.innerHTML.trim().length > 0
    ) {
      this.#wrapInCarousel(recommendationsEl);
    }
  }
  /**
   * Render JSON recommendations as carousel with compact product cards
   * @param {HTMLElement} recommendationsEl - The product-recommendations element
   * @param {Array} products - Array of product objects from JSON API
   */
  async #renderJsonRecommendations(recommendationsEl, products) {
    const loadingEl = recommendationsEl.querySelector(".cart-upsells-loading");
    if (loadingEl) {
      loadingEl.remove();
    }
    if (this.#processed || !products) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      return;
    }
    if (products.length === 0) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      const cartUpsells = recommendationsEl.closest(".cart-upsells");
      if (cartUpsells) {
        cartUpsells.style.display = "none";
      }
      return;
    }
    const cartProductIds = this.#getCartProductIds();
    const filteredProducts = products.filter(
      (product) => !cartProductIds.includes(String(product.id))
    );
    const availableItems = filteredProducts
      .map((product) => {
        const variant = this.#getFirstAvailableVariant(product);
        const isAvailable = product?.available !== false && Boolean(variant?.id);
        return isAvailable ? { product, variant } : null;
      })
      .filter(Boolean);
    if (availableItems.length === 0) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      const cartUpsells = recommendationsEl.closest(".cart-upsells");
      if (cartUpsells) {
        cartUpsells.style.display = "none";
      }
      return;
    }
    const fragment = document.createDocumentFragment();
    const slides = [];
    for (const item of availableItems.slice(0, 8)) {
      const slide = document.createElement("div");
      slide.className = "carousel-slide shrink-0";
      const cardHTML = this.#createCompactCardFromJson(item.product, item.variant);
      slide.innerHTML = cardHTML;
      slides.push(slide);
      fragment.appendChild(slide);
    }
    if (slides.length === 0) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      return;
    }
    const carouselEl = document.createElement("carousel-embla");
    carouselEl.className = "carousel cart-upsells-carousel";
    carouselEl.setAttribute("data-loop", "false");
    carouselEl.setAttribute("data-align", "start");
    carouselEl.setAttribute("data-slides-to-scroll", "1");
    const viewport = document.createElement("div");
    viewport.className = "overflow-hidden";
    viewport.setAttribute("ref", "viewport");
    const container = document.createElement("div");
    container.className = "flex gap-5";
    container.setAttribute("ref", "container");
    while (fragment.firstChild) {
      container.appendChild(fragment.firstChild);
    }
    viewport.appendChild(container);
    const carouselContainer = document.createElement("div");
    carouselContainer.className = "carousel-container relative";
    carouselContainer.appendChild(viewport);
    const arrows = document.createElement("div");
    arrows.className = "carousel-arrows";
    arrows.innerHTML = `
      <button ref="prev" type="button" class="carousel-arrow carousel-arrow-previous" aria-label="${escapeHtml(this.#getTranslation("general.previous"))}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
      </button>
      <button ref="next" type="button" class="carousel-arrow carousel-arrow-next" aria-label="${escapeHtml(this.#getTranslation("general.next"))}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
      </button>
    `;
    carouselContainer.appendChild(arrows);
    carouselEl.appendChild(carouselContainer);
    recommendationsEl.innerHTML = "";
    recommendationsEl.appendChild(carouselEl);
    this.#processed = true;
    recommendationsEl.dataset.carouselProcessed = "true";
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }
  /**
   * Create compact product card HTML from JSON product data
   * @param {Object} product - Product object from JSON API
   * @returns {string} HTML string
   */
  #getFirstAvailableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((v) => v?.available !== false) || variants[0] || null;
  }
  #createCompactCardFromJson(product, variant) {
    if (!variant?.id) {
      return "";
    }
    const image = product.featured_image || product.images?.[0];
    const priceCents = variant?.price || product.price || 0;
    const comparePriceCents = variant?.compare_at_price || product.compare_at_price || null;
    const imageUrl = image?.startsWith("//") ? `https:${image}` : image || "";
    const productUrl = product.url || `/products/${product.handle}`;
    const hasSale = comparePriceCents && comparePriceCents > priceCents;
    const priceHtml = hasSale
      ? `
        <div class="flex items-center gap-x-2">
          <span class="text-xs text-gray-500 line-through">${formatMoney(comparePriceCents)}</span>
          <span class="text-sm font-semibold">${formatMoney(priceCents)}</span>
        </div>
      `
      : `<div class="text-sm font-semibold">${formatMoney(priceCents)}</div>`;
    const plusIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    `;
    const addLabel = this.#getTranslation("cart.upsells.add_to_cart");
    return `
      <div class="product-card-compact cart-upsell-item flex flex-col gap-2">
        <div class="grid grid-cols-[50px_1fr] gap-2 items-center">
          <a href="${escapeHtml(productUrl)}" class="block">
            <div class="relative overflow-hidden rounded bg-gray-100">
              ${
                imageUrl
                  ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(
                      product.title
                    )}" class="w-12.5 h-12.5 object-cover rounded" loading="lazy" width="50" height="50">`
                  : ""
              }
            </div>
          </a>
          <div class="min-w-0 flex flex-col gap-0.5">
            <a href="${escapeHtml(productUrl)}" class="text-sm font-medium no-underline text-inherit hover:underline line-clamp-2">
              ${escapeHtml(product.title)}
            </a>
            ${priceHtml}
          </div>
        </div>
        <ajax-cart-product-form class="block">
          <form class="product-form-compact" action="/cart/add" method="post" enctype="multipart/form-data" novalidate>
            <input type="hidden" name="id" value="${variant.id}">
            <button type="submit" name="add" class="button button-outline button-sm w-full cart-upsell-add" aria-label="${escapeHtml(addLabel)}: ${escapeHtml(product.title)}">
              <span class="button-icon" aria-hidden="true">${plusIcon}</span>
              <span class="button-label">${escapeHtml(addLabel)}</span>
              <span class="button-loader hidden w-4 h-4">
                <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            </button>
            <div data-ajax-cart-errors="form" class="text-xs text-red-600 mt-1 empty:hidden"></div>
          </form>
        </ajax-cart-product-form>
      </div>
    `;
  }
  /**
   * Get product IDs currently in cart
   * @returns {string[]} Array of product IDs
   */
  #getCartProductIds() {
    if (!window.liquidAjaxCart?.cart?.items) {
      return [];
    }
    return window.liquidAjaxCart.cart.items.map((item) => String(item.product_id));
  }
  /**
   * Get translation
   * @param {string} key - Translation key
   * @returns {string} Translated text
   */
  #getTranslation(key) {
    const strings = {
      "cart.upsells.add_to_cart":
        window.Theme?.translations?.products?.addToCart || "In den Warenkorb",
      "products.product.sold_out": window.Theme?.translations?.products?.soldOut || "Ausverkauft",
      "general.previous": window.Theme?.translations?.accessibility?.previous || "Previous",
      "general.next": window.Theme?.translations?.accessibility?.next || "Next",
    };
    return strings[key] || key;
  }
  /**
   * Wrap existing recommendations grid in carousel
   * @param {HTMLElement} recommendationsEl - The product-recommendations element
   */
  #wrapInCarousel(recommendationsEl) {
    if (this.#processed) {
      return;
    }
    const existingGrid = recommendationsEl.querySelector(".grid");
    if (!existingGrid) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      return;
    }
    const productCards = Array.from(
      existingGrid.querySelectorAll("a[href*='/products/'], .product-card")
    );
    if (productCards.length === 0) {
      this.#processed = true;
      recommendationsEl.dataset.carouselProcessed = "true";
      return;
    }
    const slides = productCards.map((card) => {
      const slide = document.createElement("div");
      slide.className = "carousel-slide shrink-0";
      slide.appendChild(card);
      return slide;
    });
    const carouselEl = document.createElement("carousel-embla");
    carouselEl.className = "carousel cart-upsells-carousel";
    carouselEl.setAttribute("data-loop", "false");
    carouselEl.setAttribute("data-align", "start");
    carouselEl.setAttribute("data-slides-to-scroll", "1");
    const viewport = document.createElement("div");
    viewport.className = "overflow-hidden";
    viewport.setAttribute("ref", "viewport");
    const container = document.createElement("div");
    container.className = "flex gap-5";
    container.setAttribute("ref", "container");
    slides.forEach((slide) => container.appendChild(slide));
    viewport.appendChild(container);
    const carouselContainer = document.createElement("div");
    carouselContainer.className = "carousel-container relative";
    carouselContainer.appendChild(viewport);
    const arrows = document.createElement("div");
    arrows.className = "carousel-arrows";
    arrows.innerHTML = `
      <button ref="prev" type="button" class="carousel-arrow carousel-arrow-previous" aria-label="${escapeHtml(this.#getTranslation("general.previous"))}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
      </button>
      <button ref="next" type="button" class="carousel-arrow carousel-arrow-next" aria-label="${escapeHtml(this.#getTranslation("general.next"))}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
      </button>
    `;
    carouselContainer.appendChild(arrows);
    carouselEl.appendChild(carouselContainer);
    recommendationsEl.innerHTML = "";
    recommendationsEl.appendChild(carouselEl);
    this.#processed = true;
    recommendationsEl.dataset.carouselProcessed = "true";
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }
}
defineCustomElement("cart-upsells", CartUpsellsComponent);
